import { supabase } from '../lib/supabase';

export interface UploadedImage {
  id: string;
  url: string;
}

/**
 * Upload a base64 image to Supabase storage
 * Follows CADAM pattern: stores images in images bucket at {userId}/{conversationId}/{imageId}
 */
export async function uploadBase64Image(
  base64Data: string,
  userId: string,
  conversationId: string,
): Promise<UploadedImage> {
  console.log(`[ImageService] Starting upload for user=${userId}, conversation=${conversationId}`);
  console.log(`[ImageService] Base64 data length: ${base64Data.length}, starts with: ${base64Data.substring(0, 30)}...`);
  
  const imageId = `img-${crypto.randomUUID()}`;
  
  // Parse the base64 data URL
  const match = base64Data.match(/^data:(image\/[^;]+);base64,(.+)$/);
  if (!match) {
    console.error(`[ImageService] Invalid base64 format - no match found`);
    throw new Error('Invalid base64 image format');
  }
  
  const mimeType = match[1];
  const base64Content = match[2];
  console.log(`[ImageService] Parsed mimeType=${mimeType}, content length=${base64Content.length}`);
  
  // Convert base64 to Blob
  const byteCharacters = atob(base64Content);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  
  // Determine file extension
  const extension = mimeType.split('/')[1] || 'png';
  const filename = `${imageId}.${extension}`;
  
  // Upload to Supabase storage
  const path = `${userId}/${conversationId}/${filename}`;
  console.log(`[ImageService] Uploading to path: ${path}`);
  
  const { error, data } = await supabase.storage
    .from('images')
    .upload(path, blob, {
      contentType: mimeType,
    });
    
  if (error) {
    console.error('[ImageService] Upload failed:', error);
    throw error;
  }
  
  console.log(`[ImageService] Upload successful:`, data);
  
  // Return the image ID (filename) for the message content
  return {
    id: filename,
    url: base64Data, // Keep base64 for preview display
  };
}

/**
 * Upload a File object to Supabase storage
 */
export async function uploadImageFile(
  file: File,
  userId: string,
  conversationId: string,
): Promise<UploadedImage> {
  const imageId = `img-${crypto.randomUUID()}`;
  
  // Determine file extension from mime type
  const extension = file.type.split('/')[1] || 'png';
  const filename = `${imageId}.${extension}`;
  
  // Upload to Supabase storage
  const path = `${userId}/${conversationId}/${filename}`;
  const { error } = await supabase.storage
    .from('images')
    .upload(path, file, {
      contentType: file.type,
    });
    
  if (error) {
    console.error('Image upload error:', error);
    throw error;
  }
  
  // Create a local URL for preview
  const localUrl = URL.createObjectURL(file);
  
  return {
    id: filename,
    url: localUrl,
  };
}

/**
 * Upload multiple base64 images
 */
export async function uploadBase64Images(
  base64Images: string[],
  userId: string,
  conversationId: string,
): Promise<UploadedImage[]> {
  const results = await Promise.all(
    base64Images.map((img) => uploadBase64Image(img, userId, conversationId))
  );
  return results;
}
